import { knotsApiSdk } from '@/lib/fetcher'
import { setSlugField } from '@/utils/slug'
import * as config from '@/config'
import { toastError, toastSuccess } from '@/utils/toast'
import { ConnectorFormProps as KnotsConnectorFormProps, parseConnectorFormData } from '@knots/shared-frontend'
import { ConnectorFormProps } from './ConnectorForm.types'
import { useTranslation } from 'react-i18next'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import React from 'react'
import { Connector } from '@knots/api-sdk'
import { connectorsQueries } from '@/features/connectors'

const getFormData = (connector: Partial<Connector>) => {
  try {
    const formData = { ...parseConnectorFormData(connector?.formData) }
    if (!formData.name) formData.name = connector.name
    formData.slug = connector.slug
    return formData
  } catch {
    return {}
  }
}

const findNewConnector = (oldConnectors: Connector[], newConnectors: Connector[]) => {
  return newConnectors.find((connector) => !oldConnectors.some((oldConnector) => connector.id === oldConnector.id))
}

/**
 * Custom React hook that triggers an action when a new connector is created.
 * @param {ConnectorFormProps} props - The properties for the connector form.
 * @returns {void}
 */
export const useConnectorCreatedAction = (props: ConnectorFormProps): void => {
  const { t } = useTranslation()

  const { data } = useSuspenseQuery({
    ...connectorsQueries.list(),
    refetchOnWindowFocus: 'always',
    refetchInterval: 5000,
  })

  const connectors = data as unknown as Connector[]
  const [initialConnectors, setInitialConnectors] = React.useState(connectors)

  React.useEffect(() => {
    const isChanged = connectors.length > initialConnectors.length
    const newConnector = isChanged && findNewConnector(initialConnectors, connectors)

    if (newConnector && newConnector.readyToUse) {
      toastSuccess(t('connectors.message.connectionSuccess'))
      props.onCreated?.(newConnector)
      setInitialConnectors(connectors)
      return
    }
  }, [connectors])
}

export const useConnectorFormProps = (props: ConnectorFormProps): KnotsConnectorFormProps => {
  const queryClient = useQueryClient()

  return {
    onUrlGenerated: () => { },
    knotsApiSdk,
    createSlug: setSlugField,
    resetOnSubmit: false,
    formData: getFormData(props.connector),
    redirectUrl: `${config.PUBLIC_URL}/connector-setup-success`,
    disableEditing: props.connector.readyToUse,
    onError: () => {
      // No toast message, the fetcher already handles that
    },
    onCreated: (newConnector) => {
      queryClient.invalidateQueries(connectorsQueries.list())
      props?.onCreated?.(newConnector)
    },
  }
}
